import { HonorBadge } from "@/components/HonorBadge";
import { FounderBadge } from "@/components/FounderBadge";
import { AdminTeamBadge } from "@/components/AdminTeamBadge";
import { MyTreasureBadge } from "@/components/Treasure";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Crown, Ticket, Package, Copy, Camera, Lock, ImagePlus } from "lucide-react";
import { uploadImage } from "@/lib/imageUpload";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api, formatErr, fmtDate, imgSrc } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageExplain } from "@/components/PageExplain";
import { AddressForm, EMPTY_ADDR } from "./Rewards";
import { TxList } from "./Dashboard";
import { RoleBadge } from "@/components/RoleBadge";
import { AccountPrivacy } from "@/components/AccountPrivacy";
import { BlockedList } from "@/components/BlockedList";
import { LevelProgress, ClimberBadges } from "@/components/LevelUp";
import { DuelBadges } from "@/components/games/DuelButton";
import { QuestHeroBadge } from "@/components/quest/QuestHeroBadge";
import { AppBadge } from "@/components/AppBadge";
import { HeartBadge } from "@/components/HeartBadge";
import { NewsletterToggle, SoundToggle } from "@/components/NewsletterToggle";
import { PushPrefs } from "@/components/PushPrefs";
import { LevelFramePicker } from "@/components/LevelFramePicker";
import { VipMembershipCard } from "@/components/VipMembershipCard";
import { GoldCheck } from "@/components/GoldCheck";
import { ProfileProgress } from "@/components/ProfileProgress";
import { SupporterVisibility } from "@/components/SupporterVisibility";
import { MyMugOrder } from "@/components/merch/MyMugOrder";
import { EmailChangeCard } from "@/components/EmailChangeCard";

const STATUS = { pending: ["In Bearbeitung", "bg-amber-50 text-amber-700"], shipped: ["Verschickt", "bg-sky-50 text-sky-700"], completed: ["Abgeschlossen", "bg-emerald-50 text-emerald-700"], rejected: ["Abgelehnt", "bg-red-50 text-red-700"] };

const rank = (tx) => {
  const earned = tx.filter((t) => t.amount > 0).reduce((a, t) => a + t.amount, 0);
  if (earned >= 2000) return "Diamant";
  if (earned >= 500) return "Gold";
  return earned >= 100 ? "Silber" : "Rookie";
};

const Redemptions = ({ items }) => (
  <ul className="space-y-3" data-testid="my-redemptions-list">
    {items.length === 0 && <li className="text-sm text-slate-400 text-center py-6">Noch nichts eingelöst.</li>}
    {items.map((r) => (
      <li key={r.id} className="p-4 rounded-2xl border border-pink-100 flex flex-col gap-2">
        <div className="flex items-center justify-between gap-2">
          <p className="font-semibold text-sm flex items-center gap-2">{r.reward_type === "voucher" ? <Ticket className="w-4 h-4 text-rose-500" /> : <Package className="w-4 h-4 text-rose-500" />}{r.reward_title}</p>
          <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${STATUS[r.status][1]}`}>{STATUS[r.status][0]}</span>
        </div>
        {r.code && <button onClick={() => { navigator.clipboard?.writeText(r.code); toast.success("Code kopiert"); }} className="self-start font-mono text-sm font-bold text-rose-700 bg-rose-50 px-3 py-1.5 rounded-lg flex items-center gap-2" data-testid={`my-code-${r.id}`}>{r.code} <Copy className="w-3.5 h-3.5" /></button>}
        <p className="text-xs text-slate-400">{fmtDate(r.created_at)} · {r.cost} Punkte</p>
      </li>
    ))}
  </ul>
);

const PROFILE_STEPS = [
        "Tippe auf dein Profilbild (Kamera-Symbol) oder auf „Titelbild ändern“, um Fotos direkt vom Handy hochzuladen.",
        "Fülle Bio, Wohnort, Geburtstag und deine Social-Media-Links aus – das alles sehen andere Mitglieder auf deinem Profil (vom Geburtstag nur Tag und Monat).",
        "Speichere deine Lieferadresse – dann musst du sie beim Einlösen nicht jedes Mal neu eingeben.",
        "Unten findest du alle eingelösten Geschenke mit Status und Gutscheincodes sowie deinen kompletten Punkte-Verlauf.",
      ];

const MONTHS = ["Januar", "Februar", "März", "April", "Mai", "Juni", "Juli", "August", "September", "Oktober", "November", "Dezember"];
const sel = "h-10 rounded-xl border border-input bg-white px-3 text-sm flex-1 min-w-0";

const BirthdayField = ({ bday, setBday }) => (
  <div className="space-y-1.5">
    <Label className="text-xs">Geburtstag (nur Tag & Monat sichtbar)</Label>
    <div className="flex gap-2">
      <select data-testid="profile-bday-day" className={sel} value={bday.day} onChange={(e) => setBday({ ...bday, day: e.target.value })}>
        <option value="">Tag</option>{Array.from({ length: 31 }, (_, i) => <option key={i + 1} value={i + 1}>{i + 1}</option>)}
      </select>
      <select data-testid="profile-bday-month" className={sel} value={bday.month} onChange={(e) => setBday({ ...bday, month: e.target.value })}>
        <option value="">Monat</option>{MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
      </select>
    </div>
  </div>
);

const SOCIAL = [["instagram", "Instagram", "@deinname"], ["tiktok", "TikTok", "@deinname"], ["youtube", "YouTube", "@kanal oder Link"]];
const SocialFields = ({ links, setLinks }) => (
  <div className="space-y-2">
    <Label className="text-xs">Social Media</Label>
    {SOCIAL.map(([k, label, ph]) => (
      <div key={k} className="flex items-center gap-2">
        <span className="w-20 text-xs font-semibold text-slate-500 shrink-0">{label}</span>
        <Input data-testid={`profile-link-${k}-input`} maxLength={200} placeholder={ph} className="rounded-xl" value={links[k]} onChange={(e) => setLinks({ ...links, [k]: e.target.value })} />
      </div>
    ))}
  </div>
);

export default function Profile() {
  const { user, setUser } = useAuth();
  const [form, setForm] = useState({ name: user.name, bio: user.bio || "", location: user.location || "", links: { instagram: "", tiktok: "", youtube: "", ...(user.links || {}) } });
  const [bday, setBday] = useState({ day: user.birthday?.day || "", month: user.birthday?.month || "" });
  const coverRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const [addr, setAddr] = useState({ ...EMPTY_ADDR, ...(user.address || {}) });
  const [saving, setSaving] = useState(false);
  const [reds, setReds] = useState([]);
  const [tx, setTx] = useState([]);
  const [warnings, setWarnings] = useState([]);
  useEffect(() => {
    api.get("/redemptions").then(({ data }) => setReds(data));
    api.get("/me/warnings").then(({ data }) => setWarnings(data));
    api.get("/points/summary").then(({ data }) => setTx(data.transactions));
  }, []);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const isCover = e.target.dataset.kind === "cover";
    setUploading(true);
    try {
      const data = await uploadImage(isCover ? "/profile/cover" : "/profile/picture", file);
      setUser(data); toast.success(isCover ? "Titelbild aktualisiert 🌸" : "Profilbild aktualisiert 📸");
    } catch (err) { toast.error(formatErr(err)); } finally { setUploading(false); }
  };

  const save = async () => {
    setSaving(true);
    const birthday = bday.day && bday.month ? { day: Number(bday.day), month: Number(bday.month) } : null;
    try { const { data } = await api.put("/profile", { ...form, address: addr, birthday, clear_birthday: !birthday }); setUser(data); toast.success("Profil gespeichert"); }
    catch (e) { toast.error(formatErr(e)); } finally { setSaving(false); }
  };

  return (
    <div className="space-y-6">
      <PageExplain testId="profile-explain" steps={PROFILE_STEPS} />
      {user.email_undeliverable && <EmailChangeCard />}
      <ProfileProgress />
      <MyMugOrder />
      <div className="relative h-28 sm:h-40 rounded-3xl overflow-hidden bg-gradient-to-br from-rose-300 via-pink-400 to-fuchsia-400" data-testid="profile-cover-preview">
        {user.cover && <img src={imgSrc(user.cover)} alt="" className="w-full h-full object-cover" />}
        <button onClick={() => coverRef.current?.click()} disabled={uploading} data-testid="profile-cover-upload-btn" className="absolute bottom-3 right-3 inline-flex items-center gap-1.5 px-3 h-9 rounded-full bg-white/90 text-sm font-semibold text-slate-700 hover:bg-white"><ImagePlus className="w-4 h-4" /> Titelbild ändern</button>
        <input ref={coverRef} type="file" accept="image/*" data-kind="cover" className="hidden" onChange={upload} data-testid="profile-cover-file-input" />
      </div>
      <div className="bg-white rounded-3xl p-6 border border-pink-100 flex items-center gap-5">
        <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="profile-picture-upload-btn" className={`relative w-20 h-20 sm:w-24 sm:h-24 rounded-full shrink-0 group ${user.supporter ? "vip-ring p-[3px]" : user.level_frame ? `lvl-frame lvl-frame-${user.level_frame} p-[3px]` : "pink-gradient p-0.5"}`} data-vip={user.supporter || undefined} title="Profilbild ändern">
          {user.picture ? <img src={imgSrc(user.picture)} alt="" data-testid="profile-avatar-img" className="w-full h-full rounded-full object-cover bg-white" /> : <div className="w-full h-full rounded-full bg-white flex items-center justify-center font-display font-extrabold text-2xl text-rose-500">{user.name?.[0]?.toUpperCase()}</div>}
          <span className="absolute -bottom-1 -right-1 w-8 h-8 rounded-full bg-rose-500 text-white flex items-center justify-center border-2 border-white shadow group-hover:scale-110 transition-transform">
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
          </span>
        </button>
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={upload} data-testid="profile-picture-file-input" />
        <div className="min-w-0">
          <h1 className={`text-xl sm:text-3xl font-extrabold tracking-tight line-clamp-2 break-words ${user.supporter ? "vip-name" : ""}`} data-testid="profile-name">{user.name}{user.supporter && <GoldCheck className="w-6 h-6 sm:w-7 sm:h-7 ml-1.5 -mt-1" testId="profile-gold-check" />}</h1>
          <HonorBadge userId={user.user_id} size="lg" testId="profile-honor-badge" />
          <FounderBadge userId={user.user_id} size="lg" testId="profile-founder-badge" /> <AdminTeamBadge userId={user.user_id} size="lg" testId="profile-admin-badge" />
          <Link to={`/u/${user.user_id}`} data-testid="profile-wall-link" className="text-xs font-semibold text-rose-600 hover:underline">Mein öffentliches Profil ansehen →</Link>
          <p className="text-xs text-slate-400 truncate flex items-center gap-1" data-testid="profile-email-private"><Lock className="w-3 h-3 shrink-0" /> {user.email} · nur für dich sichtbar</p>
          {user.role === "moderator" && <div className="mt-2"><RoleBadge moderator size="lg" testId="profile-moderator-badge" /></div>}
          <p className="mt-2 inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1 rounded-full bg-amber-50 text-amber-700 border border-amber-200" data-testid="profile-rank"><Crown className="w-3.5 h-3.5" /> {rank(tx)} · {user.points} Punkte</p>
          <div className="mt-3 max-w-xs"><LevelProgress points={user.points ?? 0} /></div>
          <div className="mt-2 flex flex-wrap gap-1.5"><ClimberBadges badges={user.climber_badges} testId="my-climber-badges" /><MyTreasureBadge testId="my-treasure-badge" /><DuelBadges badges={user.duel_badges} testId="my-duel-badges" /><QuestHeroBadge items={user.quest_hero} testId="my-quest-hero-badge" shareUser={user} /><AppBadge show={!!user.app_installed_at} testId="my-app-badge" /><HeartBadge count={user.heart_count} testId="my-heart-badge" /></div>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="bg-white rounded-3xl p-6 border border-pink-100 space-y-4 min-w-0">
          <h3 className="font-bold text-lg">Profil bearbeiten</h3>
          <div className="space-y-1.5"><Label className="text-xs">Name</Label><Input data-testid="profile-name-input" className="rounded-xl" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
          <div className="space-y-1.5"><Label className="text-xs">Über mich</Label><Textarea data-testid="profile-bio-input" maxLength={300} className="rounded-xl" value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} /></div>
          <div className="space-y-1.5"><Label className="text-xs">Wohnort</Label><Input data-testid="profile-location-input" maxLength={80} placeholder="z.B. Berlin" className="rounded-xl" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} /></div>
          <BirthdayField bday={bday} setBday={setBday} />
          <SocialFields links={form.links} setLinks={(links) => setForm({ ...form, links })} />
          <h3 className="font-bold text-lg pt-2">Lieferadresse</h3>
          <AddressForm addr={addr} setAddr={setAddr} />
          <button onClick={save} disabled={saving} data-testid="profile-save-btn" className="w-full h-12 rounded-full pink-gradient text-white font-bold flex items-center justify-center gap-2">{saving && <Loader2 className="w-4 h-4 animate-spin" />} Speichern</button>
        </div>
        <div className="space-y-5 min-w-0">
          {warnings.length > 0 && (
            <div className="bg-amber-50 rounded-3xl p-6 border border-amber-200" data-testid="my-warnings">
              <h3 className="font-bold text-lg mb-3 text-amber-800">⚠️ Verwarnungen ({warnings.length})</h3>
              <ul className="space-y-2">{warnings.map((w) => <li key={w.id} className="text-sm bg-white rounded-xl p-3"><p>{w.reason}</p><p className="text-[11px] text-slate-400 mt-1">{fmtDate(w.created_at)} · von {w.by_name}</p></li>)}</ul>
              <p className="text-xs text-amber-700 mt-3">Bitte halte dich an die Community-Regeln – bei wiederholten Verstößen kann dein Konto gesperrt werden.</p>
            </div>
          )}
          {user.role !== "admin" && <VipMembershipCard user={user} />}
          {user.supporter && <SupporterVisibility />}
          <LevelFramePicker />
          <PushPrefs />
          <div className="bg-white rounded-3xl p-6 border border-pink-100"><h3 className="font-bold text-lg mb-3">Meine Geschenke</h3><Redemptions items={reds} /></div>
          <div className="bg-white rounded-3xl p-6 border border-pink-100"><h3 className="font-bold text-lg mb-1">Punkte-Verlauf</h3><TxList items={tx} /></div>
          {!user.email_undeliverable && <EmailChangeCard />}
          <SoundToggle />
          <NewsletterToggle />
          <NewsletterToggle endpoint="/profile/winback" title="„Wir vermissen dich“-E-Mail" text="Eine liebe Erinnerung mit deinem Punktestand und Bonus, wenn du 14 Tage nicht da warst." label="Erinnerungs-Mail" testid="winback" />
          <BlockedList />
          <AccountPrivacy />
        </div>
      </div>
    </div>
  );
}
